import React from "react";
import { View, StyleSheet, Linking, Pressable } from "react-native";
import { AlertCircle } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import AxioButton from "../../axio-ds/Button/AxioButton";
import Logo from "../../general/logos/Logo";
import { useAppConfig } from "../../../AppConfigProvider";
import { colors, spacing, radii } from "../../../styles/design-system";

interface LoginFormProps {
  email: string;
  password: string;
  emailError?: string;
  passwordError?: string;
  errorMessage: string;
  logo: string;
  passwordHidden: boolean;
  onEmailChangeText: (text: string) => void;
  onPasswordChangeText: (text: string) => void;
  onEmailBlur: () => void;
  onPasswordBlur: () => void;
  onPasswordToggle: () => void;
  onSubmit: () => void;
  onResetPasswordPress: () => void;
  onSignupPress: () => void;
  isLoading: boolean;
  hasSAMLSSO: boolean;
  onSAMLSSOPress: () => void;
  requireSSO: boolean;
  supportUrl: string;
}

function LoginForm({
  email,
  password,
  emailError,
  passwordError,
  errorMessage,
  logo,
  passwordHidden,
  onEmailChangeText,
  onPasswordChangeText,
  onEmailBlur,
  onPasswordBlur,
  onPasswordToggle,
  onSubmit,
  onResetPasswordPress,
  onSignupPress,
  isLoading,
  hasSAMLSSO,
  onSAMLSSOPress,
  requireSSO,
  supportUrl,
}: LoginFormProps) {
  const { primaryColor, allowFormSignup } = useAppConfig();

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Logo */}
        <View style={styles.logoWrapper}>
          <Logo uri={logo} height={40} />
        </View>

        {/* Heading */}
        <CustomText text="Sign in" style={styles.heading} />

        {/* SSO Required Subheading */}
        {hasSAMLSSO && requireSSO && (
          <CustomText text="Access your courses and assignments" style={styles.subHeading} />
        )}

        {/* Error Alert (Standard Login Only) */}
        {!requireSSO && errorMessage ? (
          <View style={styles.errorAlert}>
            <View style={styles.errorAlertContent}>
              <AlertCircle size={20} color={colors.error[600]} style={styles.alertIcon} aria-hidden="true" />
              <View style={styles.errorAlertContentFlex}>
                <CustomText text="Invalid credentials" style={styles.errorAlertTitle} />
                <CustomText text={errorMessage} style={styles.errorAlertText} />
              </View>
            </View>
          </View>
        ) : null}

        {/* Form Content */}
        <View style={styles.form}>
          {hasSAMLSSO && requireSSO ? (
            /* SSO Required View */
            <View style={styles.formGroup}>
              <AxioButton
                label="Click to sign in"
                accessibilityLabel="Press to sign in with Single Sign-On"
                variant="primary"
                disabled={isLoading}
                size="lg"
                onPress={onSAMLSSOPress}
                style={styles.signInButton}
                isLoading={isLoading}
                useTranslationText={true}
              />
            </View>
          ) : (
            /* Standard Login View */
            <>
              {/* Email Field */}
              <View style={styles.formGroup}>
                <CustomText text="Email Address" style={styles.label} />
                <CustomTextInput
                  style={[styles.input, emailError && styles.inputError]}
                  value={email}
                  placeholder="Email..."
                  onChangeText={onEmailChangeText}
                  onBlur={onEmailBlur}
                  autoComplete="email"
                  aria-label="Email text input"
                  accessibilityHint="Enter your email address"
                  error={emailError}
                  useTranslationText={true}
                />
              </View>

              {/* Password Field */}
              <View style={styles.formGroup}>
                <CustomText text="Password" style={styles.label} />
                <CustomTextInput
                  value={password}
                  style={[styles.input, passwordError && styles.inputError]}
                  placeholder="Password..."
                  onChangeText={onPasswordChangeText}
                  onBlur={onPasswordBlur}
                  secureTextEntry={passwordHidden}
                  onToggleSecureEntry={onPasswordToggle}
                  aria-label="Password text input"
                  accessibilityHint="Enter your password"
                  error={passwordError}
                  useTranslationText={true}
                />
                <View style={styles.forgotPasswordContainer}>
                  <Pressable onPress={onResetPasswordPress} accessibilityRole="link">
                    <CustomText text="Forgot your password?" style={[styles.link, { color: primaryColor }]} />
                  </Pressable>
                </View>
              </View>

              {/* Sign In Button */}
              <View style={styles.formGroup}>
                <AxioButton
                  label="Sign in"
                  accessibilityLabel="Press to sign in"
                  size="lg"
                  variant="primary"
                  style={styles.signInButton}
                  onPress={onSubmit}
                  disabled={isLoading}
                  isLoading={isLoading}
                  useTranslationText={true}
                />
              </View>

              {/* Optional SSO Button */}
              {hasSAMLSSO && (
                <View style={[styles.formGroup, { marginTop: spacing.sm }]}>
                  <AxioButton
                    label="Sign in with SSO"
                    accessibilityLabel="Press to sign in with Single Sign-On"
                    size="lg"
                    variant="secondary"
                    style={styles.signInButton}
                    onPress={onSAMLSSOPress}
                    disabled={isLoading}
                    isLoading={isLoading}
                    useTranslationText={true}
                  />
                </View>
              )}
            </>
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          {hasSAMLSSO && requireSSO ? (
            <View style={styles.footerRow}>
              <CustomText text="Having trouble signing in?" style={styles.footerText} />
              <Pressable
                accessibilityRole="link"
                onPress={() => {
                  if (supportUrl) {
                    Linking.openURL(supportUrl);
                  }
                }}>
                <CustomText text="Contact Support" style={[styles.link, { color: primaryColor }]} />
              </Pressable>
            </View>
          ) : allowFormSignup || supportUrl ? (
            allowFormSignup ? (
              <View style={styles.footerRow}>
                <CustomText text="Not a member?" style={styles.footerText} />
                <Pressable onPress={onSignupPress} accessibilityRole="link">
                  <CustomText text="Sign up" style={[styles.link, { color: primaryColor }]} />
                </Pressable>
              </View>
            ) : (
              <View style={styles.footerRow}>
                <CustomText text="Can't sign in?" style={styles.footerText} />
                <Pressable onPress={() => supportUrl && Linking.openURL(supportUrl)} accessibilityRole="link">
                  <CustomText text="Contact Support" style={[styles.link, { color: primaryColor }]} />
                </Pressable>
              </View>
            )
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.sm,
  },
  card: {
    width: "100%",
    maxWidth: 450,
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    borderRadius: radii.lg,
    padding: spacing["2xl"],
    borderWidth: 1,
    borderColor: "rgba(229, 231, 235, 0.5)",
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 30 },
    shadowOpacity: 0.12,
    shadowRadius: 48,
  },
  logoWrapper: {
    alignItems: "center",
    marginBottom: spacing.xl,
  },
  heading: {
    fontSize: 24,
    fontWeight: "600",
    color: colors.grey[900],
    textAlign: "center",
    marginBottom: spacing.xl,
  },
  subHeading: {
    fontSize: 14,
    color: colors.grey[600],
    textAlign: "center",
    marginBottom: spacing.xl,
  },
  alertIcon: {
    marginRight: spacing.xs,
    marginTop: 2,
  },
  errorAlert: {
    backgroundColor: colors.error[50],
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.error[200],
    padding: spacing.sm,
    marginBottom: spacing.lg,
  },
  errorAlertContent: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  errorAlertTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: colors.error[800],
    marginBottom: spacing.xxs,
  },
  errorAlertText: {
    fontSize: 14,
    color: colors.error[700],
  },
  form: {
    width: "100%",
  },
  formGroup: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    color: colors.grey[900],
    marginBottom: 6,
  },
  input: {
    backgroundColor: colors.white,
    borderColor: colors.grey[200],
  },
  inputError: {
    borderColor: colors.error[600],
  },
  forgotPasswordContainer: {
    marginTop: spacing.xs,
    alignItems: "flex-start",
  },
  link: {
    fontSize: 14,
    fontWeight: "500",
  },
  signInButton: {
    width: "100%",
    height: 60,
    borderRadius: radii.md,
  },
  footer: {
    alignItems: "center",
  },
  footerRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
  },
  footerText: {
    fontSize: 14,
    color: colors.grey[600],
    textAlign: "center",
    marginRight: spacing.xxs,
  },
  errorAlertContentFlex: {
    flex: 1,
  },
  ssoFormGroup: {
    marginTop: spacing.sm,
  },
});

export default React.memo(LoginForm);
